import { useState, useEffect, useRef } from "react";
import { useSessionCtx } from "../../context/SessionContext";
import { SectionHeader, StatusBadge } from "../../components/Elements";
import { MiniSwitch } from "../../components/MiniSwitch.jsx";
import { chatDisplayName, isAdminChatUserId } from "../../utils/chatIdentity.js";
import { MAX_PINNED_MESSAGES } from "./useChatModeration.js";

// Which messages each ControlTabs chat filter keeps. Approved is viewer messages only
// (admin messages never need approval); Pinned is every pinned message, the admin's too.
const CHAT_FILTERS = {
  approved: (m, isAdmin) => !isAdmin && m.isApproved === true,
  admin: (m, isAdmin) => isAdmin,
  pinned: (m) => m.pinned === true,
};

// embedded: rendered inside the MainPage accordion (hides this page's own SectionHeader).
// filter: "all" | "approved" | "admin" | "pinned" - applied by the ControlTabs radio filter.
// fill: on lg+ the box takes the full height of its panel (ControlTabs matched to the
// meeting player) instead of its own 60vh.
export default function Chat({ embedded = false, filter = "all", fill = false } = {}) {
  const [imgErrors, setImgErrors] = useState({});
  // Inline edit of one of the admin's own messages: { id, text } while open.
  const [editing, setEditing] = useState(null);
  // The edit is waiting on cme/comment/add (state for the UI, ref against a double save).
  const [savingEdit, setSavingEdit] = useState(false);
  const savingEditRef = useRef(false);
  const ctx = useSessionCtx();

  const isNearBottomRef = useRef(true);
  const inputRef = useRef(null);

  const handleScroll = () => {
    const el = ctx?.chatMessagesRef?.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 80;
  };

  // Auto-scroll to the newest message while the admin is parked at the
  // bottom. The scroll container is a DOM node reached through the context
  // ref; mutating scrollTop is intentional and must not live in React state.
  /* eslint-disable react-hooks/immutability -- DOM scroll via ctx ref is deliberate */
  useEffect(() => {
    const el = ctx?.chatMessagesRef?.current;
    if (!el || !isNearBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [ctx?.chatMessages, ctx?.chatMessagesRef]);
  /* eslint-enable react-hooks/immutability */

  if (!ctx) return null;

  const { chatReady, chatMessages, chatOnline, chatInput, setChatInput, chatMessagesRef, sendChatMsg, chatSending = false, approveMessage, togglePinned, replyingTo, startReply, cancelReply, showMsg, adminUserId, editMessage } = ctx;

  const replyTo = (messageId) => {
    startReply?.(messageId);
    inputRef.current?.focus();
  };

  // Send / Enter with nothing typed shows a validation toast instead of doing nothing.
  // The message goes to viewers only after cme/comment/add logged it: when that fails it
  // is not sent, and the text stays in the box for another try.
  const send = async () => {
    if (chatSending) return;
    if (!chatInput.trim()) {
      showMsg?.("Please type a message before sending.", "error");
      inputRef.current?.focus();
      return;
    }
    const ok = await sendChatMsg();
    if (ok === false) showMsg?.("Message not sent. Please try again.", "error");
    inputRef.current?.focus();
  };

  // togglePinned refuses a pin past the limit (returns false): say why instead of doing nothing.
  const setPinned = (messageId, pinned) => {
    if (togglePinned?.(messageId, pinned) === false) {
      showMsg?.(`You can pin up to ${MAX_PINNED_MESSAGES} messages. Unpin one to pin this message.`, "error");
    }
  };

  const startEdit = (m) => setEditing({ id: m.messageId, text: m.message });
  const cancelEdit = () => {
    if (!savingEditRef.current) setEditing(null);
  };
  // Same rules as sending: an empty edit gets a validation toast and stays open, and the new
  // text reaches viewers only after cme/comment/add logged it - when that fails the editor
  // stays open with the text for another try.
  const saveEdit = async () => {
    if (!editing || savingEditRef.current) return;
    if (!editing.text.trim()) {
      showMsg?.("Please type a message before saving.", "error");
      return;
    }
    savingEditRef.current = true;
    setSavingEdit(true);
    let result;
    try {
      result = await editMessage?.(editing.id, editing.text);
    } finally {
      savingEditRef.current = false;
      setSavingEdit(false);
    }
    if (result === false) {
      showMsg?.("This message can't be edited.", "error");
      return;
    }
    if (result === "failed") {
      showMsg?.("Edit not saved. Please try again.", "error");
      return;
    }
    setEditing(null);
  };

  const keep = CHAT_FILTERS[filter];
  const visible = keep ? chatMessages.filter((m) => keep(m, isAdminChatUserId(m.userId))) : chatMessages;

  return (
    <div className={`w-full ${fill ? "lg:h-full" : ""}`}>
      {!embedded && (
      <SectionHeader
        title="Live Chat"
        icon="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        description="Moderate messages and interact with viewers"
        badge={<StatusBadge status={chatOnline > 0 ? "active" : "inactive"}>{chatOnline} online</StatusBadge>}
      />
      )}

      {/* Compact chat box modelled on the viewer chat panel (clir-rj-cme-core/src/components/chatPanel):
          small avatar, name + time, message text; approved messages (admin ones too) get a Pin switch. */}
      {/* Embedded: fills the MainPage panel (its 60vh scroll area minus padding) so only the message list scrolls.
          fill (lg+): the whole panel, which is as tall as the meeting player. */}
      <div className={`w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col ${embedded ? `h-[calc(60vh-1.5rem)] min-h-[280px]${fill ? " lg:h-full lg:min-h-0" : ""}` : "max-w-sm h-[calc(100vh-280px)] min-h-[360px] max-h-[600px]"}`}>
        <div ref={chatMessagesRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-gray-50">
          {!chatReady && (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                <p className="text-xs">Connecting to chat...</p>
              </div>
            </div>
          )}

          {chatReady && chatMessages.length === 0 && (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p className="text-sm">No messages yet. Be the first to chat!</p>
            </div>
          )}

          {chatReady && chatMessages.length > 0 && visible.length === 0 && (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p className="text-sm">No {filter} messages</p>
            </div>
          )}

          {visible.map((m) => {
            const displayName = m.name || chatDisplayName(m.userId);
            const isAdmin = isAdminChatUserId(m.userId);
            // This admin's own message: the only kind they can edit.
            const isOwn = isAdmin && adminUserId != null && m.userId === adminUserId;
            const isEditing = editing?.id === m.messageId;
            const isPending = !isAdmin && !m.isApproved;
            // Each message is its own card: pending viewer messages get a dashed amber
            // border so they stand out until approved; admin messages a light gray card.
            const cardTone = isPending
              ? "bg-white border-dashed border-amber-300"
              : isAdmin
                ? "bg-gray-100 border-gray-200"
                : "bg-white border-gray-200";
            return (
              <div
                key={m.messageId}
                data-testid="chat-message-card"
                data-state={isAdmin ? "admin" : isPending ? "pending" : "approved"}
                className={`flex gap-3 rounded-lg border p-3 shadow-sm ${cardTone}`}
              >
                <div className="w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 shrink-0 overflow-hidden uppercase">
                  {m.image && !imgErrors[m.messageId] ? (
                    <img src={m.image} alt={displayName} className="w-full h-full object-cover" onError={() => setImgErrors((prev) => ({ ...prev, [m.messageId]: true }))} />
                  ) : (
                    (displayName || "?")[0]
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-gray-900 truncate">{displayName}</span>
                    {/* Dark filled pill so it stands out on the gray admin card */}
                    {isAdmin && <span className="shrink-0 rounded-full bg-gray-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-white">Admin</span>}
                    <span className="text-xs text-gray-400 shrink-0">{new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    {isOwn && !isEditing && (
                      <button
                        type="button"
                        onClick={() => startEdit(m)}
                        aria-label="Edit message"
                        title="Edit message"
                        className="ml-auto shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-white hover:text-gray-700"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                    {!isAdmin && (
                      <div className="ml-auto shrink-0">
                        <MiniSwitch checked={m.isApproved} onChange={(v) => approveMessage(m.messageId, v)} label="Approve" hint={m.isApproved ? "Approved: visible to viewers" : "Pending: approve to show it to viewers"} />
                      </div>
                    )}
                  </div>
                  {m.replyTo && (
                    <div className="mb-1 border-l-2 border-gray-300 pl-2 text-xs text-gray-500">
                      <span className="font-medium text-gray-600">{m.replyTo.name || "Viewer"}</span>
                      <p className="truncate">{m.replyTo.message}</p>
                    </div>
                  )}
                  {isEditing ? (
                    // Enter saves, Escape cancels - same keys as the composer.
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        aria-label="Edit message text"
                        value={editing.text}
                        autoFocus
                        readOnly={savingEdit}
                        aria-busy={savingEdit || undefined}
                        onChange={(e) => setEditing((prev) => prev && { ...prev, text: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            saveEdit();
                          } else if (e.key === "Escape") {
                            cancelEdit();
                          }
                        }}
                        className="flex-1 min-w-0 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg outline-none focus:border-gray-400"
                      />
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={savingEdit}
                        className="shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-full border bg-gray-900 text-white border-gray-900 hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {savingEdit ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={savingEdit}
                        className="shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-full border text-gray-600 border-gray-200 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    /* Pending viewer messages are dimmed until approved */
                    <p className={`text-sm break-words ${!isAdmin && !m.isApproved ? "text-gray-400" : "text-gray-700"}`}>{m.message}</p>
                  )}
                  {/* Under the message once it is approved (live for viewers): Pin on every message,
                      the admin's own included (admin messages are always approved); Reply on viewer
                      messages only. */}
                  {m.isApproved && (
                    <div data-testid="message-actions" className="mt-1.5 flex items-center gap-3">
                      <MiniSwitch
                        checked={m.pinned === true}
                        onChange={(v) => setPinned(m.messageId, v)}
                        label="Pin"
                        hint={m.pinned ? "Pinned: sticks to the top of the viewer chat" : "Pin to the top of the viewer chat"}
                      />
                      {!isAdmin && startReply && (
                        <button
                          type="button"
                          onClick={() => replyTo(m.messageId)}
                          title="Reply to this message as admin"
                          className={`text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                            replyingTo?.messageId === m.messageId ? "bg-gray-900 text-white border-gray-900" : "text-gray-600 border-gray-200 hover:bg-gray-100"
                          }`}
                        >
                          Reply
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-3 border-t border-gray-200 shrink-0">
          {replyingTo && (
            <div data-testid="reply-banner" className="mb-2 flex items-start gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-1.5">
              <div className="min-w-0 flex-1 text-xs">
                <span className="text-gray-500">Replying to </span>
                <span className="font-medium text-gray-800">{replyingTo.name || chatDisplayName(replyingTo.userId)}</span>
                <p className="truncate text-gray-500">{replyingTo.message}</p>
              </div>
              <button type="button" onClick={cancelReply} aria-label="Cancel reply" className="shrink-0 text-gray-400 hover:text-gray-700 text-sm leading-none">
                ✕
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                } else if (e.key === "Escape" && replyingTo) {
                  cancelReply?.();
                }
              }}
              placeholder={replyingTo ? "Write a reply..." : "Send a message as admin..."}
              // Read-only (not disabled) while sending, so it keeps focus for the next message.
              readOnly={chatSending}
              aria-busy={chatSending || undefined}
              className={`flex-1 min-w-0 px-4 py-2 text-sm bg-white border border-gray-200 rounded-full outline-none focus:outline-none focus:ring-0 focus:border-gray-200 ${chatSending ? "text-gray-400" : ""}`}
            />
            <button
              type="button"
              onClick={send}
              disabled={chatSending}
              className="px-5 py-2 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {chatSending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
